import publicationService from 'services/PublicationService';
import ActivityService from 'services/ActivityService';
import { ACTIVITY_TYPE } from 'services/ActivityService/activityTypes';
import { loadSession } from 'data/CurrentUser/sessionManager';
import { dispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';
import messages from '../messages';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { sortByDate } from 'Workspace/Publications/utils';

import appConfig from 'appConfig';
import { getSBMediaGridLineItem, mediaImportNotes } from 'Workspace/Cohorts/components/CohortDeploymentsList/BronteViewer/mediaImportData';
import { tableOfContents } from 'Workspace/Publications/utils/constants';

const showToastError = (message) => { dispatch(pushToast({ message: intl.formatMessage(message) })); };

export const decodeResponse = async (response) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let result = '';
  let done = false;

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;
    result += decoder.decode(value || new Uint8Array(), { stream: !done });
  }

  return result
    .trim()
    .split('\n')
    .map((line) => line.split(',').map((v) => v.trim()));
};

/**
* Fetches SB Media Grid data for a given activity.
* @param {string} activityId - Unique ID of the activity/publication.
* @param {string} outputType - Output type to request from the backend.
* @returns {Promise<Array<Object>>} - Resolves to an array of media grid rows.
* @throws {Error} - If the network request fails or returns invalid data.
*/
export const fetchSBMediaGridData = async (activityId, outputType, market) => {
  try {
    const { publications } = await publicationService.getPublicationHistory(activityId, outputType, market);

    if (!publications?.length) {
      return showToastError(messages.noPublicationHistory);
    }
    // Sort the publictions for the latest one 
    const sortedPublications = sortByDate(publications);
    const { publicationId, cohortId, deploymentId, description = '{}' } = sortedPublications[0];

    if (!cohortId || !deploymentId) {
      return showToastError(messages.invalidSBMediaGridOption);
    }

    const deploymentDetails = await publicationService.getDeploymentDetails(cohortId, deploymentId);

    if (!deploymentDetails) return;

    if (!deploymentDetails.isScored) {
      const env = appConfig.getEnv();
      const csvData = [...mediaImportNotes];
      const publicationDetails = JSON.parse(description);
      let elementsData = [];

      // Handle different table of contents (TOC) structures for unscored deployments.
      // The TOC level determines the hierarchy depth and which child activities need to be fetched
      // for generating the SB Media Grid data.
      // 
      // - For TWO_LEVELS structure: Publication -> Lessons
      //   Fetch direct child activities of type LESSON at depth level 1.
      //   These represent the top-level lessons within the publication.
      // 
      // - For THREE_LEVELS structure: Publication -> Lessons -> Screens
      //   Fetch child activities of type 'screen' at depth level 2.
      //   This retrieves screens nested within lessons, which are the actual content elements
      //   that need to be included in the media grid for three-level publications.
      if (publicationDetails?.tocLevel === tableOfContents.TWO_LEVELS) {
        elementsData = await ActivityService.getChildActivitiesByType(
          activityId,
          ACTIVITY_TYPE.LESSON,
          1
        );
      } else if (publicationDetails?.tocLevel === tableOfContents.THREE_LEVELS) {
        elementsData = await ActivityService.getChildActivitiesByType(
          activityId,
          'screen',
          2
        );
      }

      const excelLineItems = elementsData.map((element) =>
        getSBMediaGridLineItem(
          element,
          deploymentId,
          cohortId,
          env,
          publicationDetails?.tocLevel
        )
      );

      csvData.push(...excelLineItems);
      return csvData;
    }
    const token = loadSession();
    const baseUrl = appConfig.getSBMediaGridURL();
    const url = `${baseUrl}${publicationId}`;

    //Fetch the SB Media Grid file
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 500) {
        return showToastError(messages.internalServerErrorMessage);
      }
      if (response.status === 404) {
        return showToastError(messages.sbMediaGridDownloadFail);
      }
      throw new Error(`Unexpected error: ${response.status}`);
    }

    return await decodeResponse(response);

  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error downloading media import file:', error);
  }
};
